import { useEffect, useCallback } from 'react';
import { Keyboard } from 'lucide-react';

/**
 * SectionTiles — Homepage-like tile grid for page sub-sections.
 * Supports 1-9,0 keyboard shortcuts. Esc goes back to tiles (intercepted before global handler).
 */
export function SectionTiles({ sections, activeSection, onSelect, pageTitle, pageDescription, children, headerActions }) {
  const handleKeyDown = useCallback((e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT' || e.target.isContentEditable) return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    // Escape: if inside a section, go back to tiles
    if (e.key === 'Escape' && activeSection) {
      const openDialog = document.querySelector('[role="dialog"]');
      if (openDialog) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      window.__sectionTilesEscHandled = Date.now();
      onSelect('');
      return;
    }
    // Number keys select sections (only when on tiles view)
    const keyMap = { '1': 0, '2': 1, '3': 2, '4': 3, '5': 4, '6': 5, '7': 6, '8': 7, '9': 8, '0': 9 };
    const idx = keyMap[e.key];
    if (idx !== undefined && idx < sections.length && !activeSection) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      window.__sectionTilesEscHandled = Date.now();
      onSelect(sections[idx].value);
    }
  }, [activeSection, onSelect, sections]);

  useEffect(() => {
    // Use capture phase to fire before the global keydown handler in useKeyboardShortcuts
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);

  if (!activeSection) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-8 sm:py-16" data-testid="section-tiles">
        <div className="text-center mb-6 shrink-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight mb-0.5">{pageTitle}</h1>
          {pageDescription && <p className="text-muted-foreground text-sm">{pageDescription}</p>}
        </div>
        <div
          className="flex flex-wrap justify-center mx-auto"
          style={{ gap: 'clamp(10px, 2vw, 14px)', maxWidth: '620px' }}
        >
          {sections.map((s, idx) => {
            const Icon = s.icon;
            const shortcut = idx < 9 ? String(idx + 1) : idx === 9 ? '0' : null;
            return (
              <button
                key={s.value}
                onClick={() => onSelect(s.value)}
                className="group flex flex-col items-center justify-center rounded-2xl
                  bg-card border border-border/50
                  shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                  hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]
                  hover:border-border
                  hover:-translate-y-0.5
                  active:translate-y-0 active:shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                  transition-all duration-200 ease-out cursor-pointer"
                style={{
                  width: 'clamp(110px, 22vw, 140px)',
                  aspectRatio: '1',
                  gap: 'clamp(4px, 1vh, 10px)',
                  padding: 'clamp(10px, 2vh, 20px) clamp(8px, 1.5vw, 16px)'
                }}
                data-testid={`section-tile-${s.value}`}
              >
                <div
                  className="rounded-xl bg-secondary/80 flex items-center justify-center
                    group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200"
                  style={{ width: 'clamp(36px, 6vh, 56px)', height: 'clamp(36px, 6vh, 56px)' }}
                >
                  <Icon style={{ width: 'clamp(18px, 3vh, 28px)', height: 'clamp(18px, 3vh, 28px)' }} strokeWidth={1.5} />
                </div>
                <span className="font-medium text-foreground/80 group-hover:text-foreground transition-colors text-center"
                  style={{ fontSize: 'clamp(0.65rem, 1.3vh, 0.85rem)' }}>
                  {s.label}
                </span>
                {shortcut && (
                  <kbd className="text-muted-foreground/60 bg-muted rounded font-mono"
                    style={{ fontSize: 'clamp(0.5rem, 0.8vh, 0.6rem)', padding: '1px clamp(3px, 0.5vw, 6px)' }}>
                    {shortcut}
                  </kbd>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-2 text-muted-foreground/50 mt-4"
          style={{ fontSize: 'clamp(0.55rem, 1vh, 0.75rem)' }}>
          <Keyboard style={{ width: 'clamp(10px, 1.5vh, 14px)', height: 'clamp(10px, 1.5vh, 14px)' }} />
          <span>Press <kbd className="bg-muted rounded font-mono" style={{ padding: '0 3px', fontSize: 'inherit' }}>1</kbd>-<kbd className="bg-muted rounded font-mono" style={{ padding: '0 3px', fontSize: 'inherit' }}>{Math.min(sections.length, 9)}</kbd> to navigate</span>
        </div>
      </div>
    );
  }

  const current = sections.find(s => s.value === activeSection);
  return (
    <div className="space-y-4" data-testid={`section-${activeSection}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSelect('')}
            className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
            data-testid="section-back-btn"
          >
            {pageTitle}
          </button>
          <span className="text-muted-foreground/50">/</span>
          <h1 className="text-lg sm:text-xl font-bold">{current?.label || activeSection}</h1>
        </div>
        {headerActions}
      </div>
      {children}
    </div>
  );
}
